import { normalizePath, useSetFlowTo } from 'Simple/Flow'
import {
  notifyError,
  notifySuccess,
  useNotifications,
} from 'Logic/Notifications'
import queryEventByPK from './query-event-by-pk.graphql.js'
import insertEventMutation from './insert-event-mutation.graphql.js'
import updateEventMutation from './update-event-mutation.graphql.js'
import updateEventStatusMutation from './update-event-status-mutation.graphql.js'
import copyEventsMutation from './copy-events-mutation.graphql.js'
import { useClient, useMutation } from 'Data/Api.js'
import { useDataChange, useDataValue } from 'Simple/Data.js'
import { PutObjectCommand } from '@aws-sdk/client-s3'
import {
  useAwsCredentials,
  useAwsS3Client,
  getS3ObjectUrl,
  getAmazonS3URI,
} from 'Data/s3'
import { v4 as uuid } from 'uuid'

let INITIAL_SELECTED_STATE = {
  event_id: null,
  event_name: null,
  event_type: null,
  event_status: null,
  automation_type: null,
  trigger_type: 'post',
  tx_status: [],
  order_status: [],
  appointment_status: [],
  appointment_type_ids: [],
  appointment_type_names: [],
  actions: [],
  source_types: [],
  source: null,
  is_integration: false,
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data) {
  let [, executeInsertEventMutation] = useMutation(insertEventMutation)
  let [, executeUpdateEventMutation] = useMutation(updateEventMutation)
  let [, executeCopyEventsMutation] = useMutation(copyEventsMutation)
  let [, executeUpdateEventStatusMutation] = useMutation(
    updateEventStatusMutation
  )
  let client = useClient()
  let getCredentials = useAwsCredentials(props)
  let s3 = useAwsS3Client(props)

  let parent_company_id = useDataValue({
    context: 'global',
    path: 'current_location.parent_company._id',
    viewPath: props.viewPath,
  })

  let setFlowTo = useSetFlowTo(props.viewPath)

  let setEventId = useDataChange({
    context: 'flow_shortcuts',
    path: 'eventId',
    viewPath: props.viewPath,
  })

  let [, notify] = useNotifications()

  function resetData(change) {
    change(next => {
      next.selected = {
        ...next.selected,
        ...INITIAL_SELECTED_STATE,
      }
      next.original_value = INITIAL_SELECTED_STATE
    })
    setEventId(null)
  }

  async function openEditAutomationScreen(event_id, change) {
    let response = await client
      .query(queryEventByPK, { id: event_id })
      .toPromise()

    if (response.error || !response.data?.events_events_by_pk) {
      notify(notifyError('An error has occurred trying to get the automation'))
      return
    }

    let data = response.data.events_events_by_pk
    let original_value = {
      event_id,
      event_name: data.name,
      event_type: data.type,
      event_status: data.status,
      location_id: data.organization_id,
      automation_type: data.automation_type,
      trigger_type: data.trigger_type,
      tx_status: data.type === 'txs_status_update' ? data.data.value_to : null,
      appointment_status:
        data.type === 'appointment_bookings_state_update'
          ? data.data.value_to
          : null,
      appointment_type_ids:
        data.type === 'appointment_bookings_state_update'
          ? data.data.appointment_type_ids
          : null,
      source_types:
        data.type === 'contract_signed' ||
        data.type === 'appointment_bookings_state_update' ||
        data.type === 'payment_made' ||
        data.type === 'payment_method_updated'
          ? data.data.source_types
          : null,
      actions: data.actions,
      tx_status_name:
        data.type === 'txs_status_update' ? data.data.status_name : null,
      appointment_type_names:
        data.type === 'appointment_bookings_state_update'
          ? data.data.appointment_type_names
          : null,
      custom_resource_type:
        data.type === 'custom_resource_insert'
          ? data.data.custom_resource_type
          : null,
      order_status:
        data.type === 'order_status_changed' ? data.data.value_to : null,
      app_id: data.app?.id || null,
      app: data.app,
      is_integration: !!data.source && data.source !== 'user',
    }

    change(next => {
      next.selected = {
        ...next.selected,
        ...original_value,
      }
      next.original_value = original_value
    })

    setEventId(event_id)
    setFlowTo(normalizePath(props.viewPath, 'Automation'))
  }

  function openNewAutomationScreen(change) {
    resetData(change)
    setFlowTo(normalizePath(props.viewPath, 'Automation'))
  }

  function getTemplatePath({ parent_company_id, file_key }) {
    return `companies/${parent_company_id}/communication-templates/${file_key}.html`
  }

  async function uploadTemplate(content) {
    let file_key = uuid()
    let credentials = await getCredentials()
    let destination_key = getTemplatePath({ parent_company_id, file_key })
    try {
      await (
        await s3()
      ).send(
        new PutObjectCommand({
          Bucket: credentials.storage_bucket_name,
          Key: destination_key,
          Body: content,
          ContentType: 'text/html',
        })
      )
    } catch (error) {
      return notify(
        notifyError(`An error has occurred while uploading new template`)
      )
    }
    return getS3ObjectUrl({
      bucket: credentials.storage_bucket_name,
      key: destination_key,
      region: credentials.region,
    })
  }

  async function saveNewAutomation(automation_data, change) {
    let { id, ...object } = processEventData(automation_data, true)

    // check if comms action is there, and save templates to s3
    let newActions = await Promise.all(
      object.actions.data.map(async (action, index) => {
        switch (action.type) {
          case 'send_communication': {
            let email_template = null
            if (action.data?.email_template?.content) {
              email_template = {
                url: await uploadTemplate(action.data.email_template.content),
                subject: action.data.email_template.subject,
                from: action.data.email_template.from,
                advanced_editor: action.data.email_template.advanced_editor,
              }
            }

            let sms_template = null
            if (action.data?.sms_template?.content) {
              sms_template = {
                url: await uploadTemplate(action.data.sms_template.content),
                from: action.data.sms_template.from,
              }
            }
            return {
              ...action,
              data: {
                ...action.data,
                sms_template,
                email_template,
              },
              order: index,
            }
          }
          default: {
            return {
              ...action,
              order: index,
            }
          }
        }
      })
    )

    object = { ...object, actions: { data: newActions } }

    let response = await executeInsertEventMutation({
      object,
    })

    if (response.error) {
      notify(notifyError('An error has occurred when creating automation'))
      return
    }
    resetData(change)
    notify(
      notifySuccess(
        'Automation added! Automation has been created for this location.'
      )
    )
    setFlowTo(normalizePath(props.viewPath, 'AutomationsList'))
  }

  async function updateAutomation(value, change) {
    let event = processEventData(value.selected)
    let response = await executeUpdateEventMutation({
      ...event,
      actions: event.actions.data,
    })

    if (response.error) {
      return notify(
        notifyError('An error has occurred when updating automation')
      )
    }

    notify(notifySuccess('Automation updated!'))
    resetData(change)
    setFlowTo(normalizePath(props.viewPath, 'AutomationsList'))
  }

  async function toggleAutomationStatus({
    event_id,
    status,
    event_type,
    data,
    organization_id,
    automation_type,
    automation_hub_enabled,
  }) {
    if (!automation_hub_enabled) return

    let response = await executeUpdateEventStatusMutation({
      event_id,
      update_other_statuses: status === 'active',
      status,
      event_type,
      data,
      organization_id,
      automation_type,
    })
    if (response.error) {
      notify(
        notifyError('An error has occurred changing the automation status')
      )
      return
    }
    notify(
      notifySuccess(
        `${
          status === 'active'
            ? 'Now Running! Automation has been set to active for this location.'
            : 'Stopped! Automation has been paused for this location.'
        }`
      )
    )
  }

  async function copyAutomations(value, change) {
    let { copy_to_locations, automations } = value.selected
    if (!automations.length || !copy_to_locations?.length) return

    let response = await executeCopyEventsMutation({
      event_ids: automations,
      target_location_ids: copy_to_locations,
    })

    if (response.error) {
      return notify(
        notifyError('An error has occurred when copying automations')
      )
    }

    let length = copy_to_locations.length
    notify(
      notifySuccess(
        `Automations copied! Automations were copied to ${length} ${
          length === 1 ? 'location' : 'locations'
        }.`
      )
    )

    change(next => {
      next.selected.copy_to_locations = []
      next.selected.automations = []
    })
  }

  return async function onSubmit({ value, originalValue, args, change }) {
    try {
      switch (args.type) {
        case 'edit': {
          await openEditAutomationScreen(args.event_id, change)
          break
        }
        case 'new': {
          openNewAutomationScreen(change)
          break
        }
        case 'save': {
          await saveNewAutomation(value.selected, change)
          break
        }
        case 'update': {
          await updateAutomation(value, change)
          break
        }
        case 'toggleStatus': {
          await toggleAutomationStatus({
            ...args,
            automation_hub_enabled: value.selected.automation_hub_enabled,
          })
          break
        }
        case 'copy': {
          await copyAutomations(value, change)
          break
        }
        case 'cancel':
        case 'back': {
          resetData(change)
          setEventId(null)
          setFlowTo(normalizePath(props.viewPath, 'AutomationsList'))
          break
        }
        default:
          break
      }
    } catch (error) {
      notify(notifyError('An error has occurred'))
    }
  }
}

function generateData(event) {
  switch (event.event_type) {
    case 'appointment_bookings_state_update':
      return {
        value_to: event.appointment_status,
        appointment_type_ids: event.appointment_type_ids,
        appointment_type_names: event.appointment_type_names,
        source_types: event.appointment_status.includes('SCHEDULED')
          ? event.source_types
          : [],
      }
    case 'txs_status_update':
      return {
        value_to: event.tx_status,
        status_name: event.tx_status_name,
      }
    case 'custom_resource_insert':
      return {
        custom_resource_type: event.custom_resource_type,
        app_id: event.app_id,
      }
    case 'order_status_changed':
      return {
        value_to: event.order_status,
      }
    case 'contract_signed':
      return {
        source_types: event.source_types,
      }
    case 'document_signed':
      return {
        document_template_ids: event.document_template_ids,
      }
    case 'lead_status_changed':
      return {
        value_to: ['created'], // this value is fixed for leads, all automation will react to same status.
      }
    case 'medical_form_submitted':
      return {}
    case 'payment_made':
    case 'payment_method_updated':
      return {
        source_types: event.source_types,
      }
    default:
      return {}
  }
}

function generateActions(actions, dropIds = false) {
  return Array.isArray(actions)
    ? {
        data: actions.map(({ id, type, data, order, name }) => {
          if (dropIds) {
            return { type, data, order, name }
          }
          return { id, type, data, order, name }
        }),
      }
    : {}
}

let EVENT_TYPE_DELAYS = /** @type {const} */ ({
  lead_status_changed: { weeks: 0, days: 0, hours: 0, minutes: 2 },
})

function processEventData(eventData, dropIds = false) {
  return {
    id: eventData.event_id,
    name: eventData.event_name,
    organization_id: eventData.location_id,
    type: eventData.event_type,
    automation_type: eventData.automation_type,
    trigger_type: eventData.trigger_type,
    data: generateData(eventData),
    actions: generateActions(eventData.actions, dropIds),
    delay: EVENT_TYPE_DELAYS[eventData.event_type] ?? null,
  }
}
